/**
 * Delivery for session traces. Recording remains in `audio/trace.ts`; this module owns
 * the network policy, retry record, and the deliberate whole-device broadcast.
 */

import { previewEnvelope, previewTrace } from './session/preview-trace';
import { jsonBytes } from './session/preview-schema';
import type { PreviewChunk } from './session/preview-schema';
import type { SessionTrace } from './audio/trace';
import { apiOrigin } from './config/origin';
import { settings } from './state/settings';

// Resolved per call rather than at module load: the native shell serves this page from
// https://localhost, where a relative path reaches the APK's own asset bundle and 404s.
// Nothing then reports — no telemetry, no trace, no way to tell a silent app from an
// unused one. `apiOrigin()` is the empty string on the web, so the URL is unchanged there.
const endpoint = (): string => `${apiOrigin()}/api/diagnostics`;
export const UPLOADED_KEY = 'brahm.diagnostics-uploaded.v1';
export const MAX_UPLOADED_IDS = 200;

export type UploadResult = 'uploaded' | 'disabled' | 'already-uploaded'
  | 'already-attempted' | 'missing-session' | 'deferred' | 'failed';
export type DiagnosticsNotice = 'sending' | 'sent' | 'failed' | 'skipped';

export type BroadcastResult =
  | { stored: true; key: string }
  | { stored: false; reason: string };

interface Dependencies {
  request: typeof fetch;
  storage: () => Storage | null;
  enabled: () => boolean;
  fullLogEnabled: () => boolean;
  userAgent: () => string | null;
  exportedAt: () => string;
}

function browserStorage(): Storage | null {
  try {
    const value: unknown = typeof localStorage === 'undefined' ? null : localStorage;
    if (!value || typeof (value as Storage).getItem !== 'function'
        || typeof (value as Storage).setItem !== 'function') return null;
    return value as Storage;
  } catch {
    return null;
  }
}

const defaults: Dependencies = {
  request: (...args) => fetch(...args),
  storage: browserStorage,
  enabled: () => settings.value.uploadDiagnostics,
  fullLogEnabled: () => settings.value.uploadFullLog,
  userAgent: () => typeof navigator === 'undefined' ? null : navigator.userAgent,
  exportedAt: () => new Date().toISOString(),
};

function uploadedIds(store: Storage | null): string[] {
  if (!store) return [];
  try {
    const parsed: unknown = JSON.parse(store.getItem(UPLOADED_KEY) ?? '[]');
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === 'string').slice(0, MAX_UPLOADED_IDS)
      : [];
  } catch {
    return [];
  }
}

function rememberUploaded(store: Storage | null, id: string): void {
  if (!store) return;
  try {
    const ids = [id, ...uploadedIds(store).filter((seen) => seen !== id)]
      .slice(0, MAX_UPLOADED_IDS);
    store.setItem(UPLOADED_KEY, JSON.stringify(ids));
  } catch {
    /* Private mode must not turn diagnosis into a playback failure. */
  }
}

/**
 * A page gets one attempt per session. A transport failure stays unmarked, so creating
 * the next page instance retries it; repeated lifecycle callbacks in this page do not
 * hammer an endpoint that is already known to be unreachable.
 */
export function createDiagnosticsUploader(overrides: Partial<Dependencies> = {}) {
  const deps = { ...defaults, ...overrides };
  const attempted = new Set<string>();
  const pending = new Map<string, Promise<UploadResult>>();

  const uploadBody = (
    session: { id: string },
    body: string,
    mode: 'session' | 'broadcast' | 'preview',
  ): Promise<UploadResult> => {
    if (!deps.enabled()) return Promise.resolve('disabled');
    const store = deps.storage();
    if (uploadedIds(store).includes(session.id)) return Promise.resolve('already-uploaded');
    const active = pending.get(session.id);
    if (active) return active;
    if (attempted.has(session.id)) return Promise.resolve('already-attempted');
    attempted.add(session.id);

    const run = (async (): Promise<UploadResult> => {
      try {
        // sendBeacon and fetch keepalive share an approximately 64 KiB ceiling. A trace
        // can exceed it, so use an ordinary fetch; if navigation cuts it off, the durable
        // unmarked session is retried by the next-launch sweep.
        const result = await sendAcknowledgedDiagnostics(body, mode, deps.request);
        if (!result.stored) return 'failed';
        rememberUploaded(store, session.id);
        return 'uploaded';
      } catch {
        return 'failed';
      } finally {
        pending.delete(session.id);
      }
    })();
    pending.set(session.id, run);
    return run;
  };

  const upload = (session: SessionTrace): Promise<UploadResult> => uploadBody(
    session,
    JSON.stringify({
      exportedAt: deps.exportedAt(),
      userAgent: deps.userAgent(),
      sessions: [session],
    }),
    'session',
  );

  return {
    upload,
    async uploadPreview(chunk: PreviewChunk, canUpload = () => true): Promise<UploadResult> {
      try {
        if (!canUpload()) return 'deferred';
        const envelope = await previewEnvelope(chunk);
        // Hashing yields: Sit may have been pressed since this idle sweep began.
        if (!canUpload()) return 'deferred';
        if (jsonBytes(envelope) > 300 * 1024) return 'failed';
        return uploadBody(envelope.sessions[0], JSON.stringify(envelope), 'preview');
      } catch { return 'failed'; }
    },
    uploadClosed(session: SessionTrace, fullEnvelope: string): Promise<UploadResult> {
      if (!deps.fullLogEnabled()) return upload(session);
      // Broadcast objects are addressed by the sessions' content on the Worker. Sending
      // the same device-held log again therefore replaces no evidence and creates no new
      // object, while a changed entry gets a different key rather than overwriting it.
      return uploadBody(session, fullEnvelope, 'broadcast');
    },
    async sweep(sessions: SessionTrace[]): Promise<UploadResult[]> {
      return Promise.all(sessions.map(upload));
    },
  };
}

const automatic = createDiagnosticsUploader();

/** Queue a closed sit without putting a network await on the audio path. */
export function uploadClosedSession(
  session: SessionTrace | undefined,
  fullEnvelope = '',
): Promise<UploadResult> {
  if (!session) return Promise.resolve('missing-session');
  return automatic.uploadClosed(session, fullEnvelope);
}

/** Queue every durable sit after startup has annotated any interrupted predecessor. */
export function sweepStoredSessions(sessions: SessionTrace[]): void {
  void automatic.sweep(sessions);
}

/**
 * Send the exact whole-device export and wait for the acknowledgement a person pressing
 * the button is entitled to. This deliberately ignores the automatic-upload setting:
 * the explicit button press is the instruction to send this one export.
 */
export async function broadcastDiagnostics(
  body: string,
  request: typeof fetch = (...args) => fetch(...args),
): Promise<BroadcastResult> {
  return sendAcknowledgedDiagnostics(body, 'broadcast', request);
}

async function sendAcknowledgedDiagnostics(
  body: string,
  mode: 'session' | 'broadcast' | 'preview',
  request: typeof fetch,
): Promise<BroadcastResult> {
  try {
    const suffix = mode === 'broadcast' ? '?ack=1' : `?ack=1&mode=${mode}`;
    const response = await request(`${endpoint()}${suffix}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
    });
    const parsed = await response.json().catch(() => null) as {
      stored?: unknown; key?: unknown; error?: unknown;
    } | null;
    if (response.ok && parsed?.stored === true && typeof parsed.key === 'string') {
      return { stored: true, key: parsed.key };
    }
    const reason = typeof parsed?.error === 'string'
      ? parsed.error
      : `the service answered ${response.status}`;
    return { stored: false, reason };
  } catch (error) {
    return { stored: false, reason: error instanceof Error ? error.message : String(error) };
  }
}

export function diagnosticsNotice(result: UploadResult): Exclude<DiagnosticsNotice, 'sending'> {
  if (result === 'uploaded') return 'sent';
  if (result === 'failed') return 'failed';
  return 'skipped';
}

/** Each sealed part has its own identity; an earlier acknowledgement cannot hide later taps. */
export function sweepPreviewChunks(canUpload: () => boolean): void {
  for (const chunk of previewTrace.sealed()) void automatic.uploadPreview(chunk, canUpload);
}
